import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { BackLink } from 'compositions/BackLink'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import {
  useAddGroupContact,
  useContactGroup,
  useDeleteContactGroup,
  useRemoveGroupContact,
  useUpdateContactGroup,
} from 'hooks/api'
import { Pencil, Save, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import type { ContactGroupDetail, Ruleset } from 'types/communications'
import type { Contact } from 'types/contacts'
import { formatPhone } from 'utils/phone'

import { RuleBuilder } from './rule-builder'
import { StaticContactPicker } from './static-contact-picker'
const DEFAULT_RULESET: Ruleset = { match: 'all', rules: [] }

function contactDisplayName(contact: { firstName?: string | null; lastName?: string | null; email?: string | null }) {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || '(unnamed)'
}

function syncFromGroup(group: ContactGroupDetail) {
  return {
    name: group.name,
    ruleset: (group.rules as Ruleset) ?? DEFAULT_RULESET,
    selectedContacts: group.contacts.map((c) => c.contact),
  }
}

function MemberTable({ group }: { group: ContactGroupDetail }) {
  if (group.contacts.length === 0) {
    return <p className="text-sm text-muted-foreground">No contacts in this list.</p>
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Name</th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Email</th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Phone</th>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Organization</th>
          </tr>
        </thead>
        <tbody>
          {group.contacts.map((member) => (
            <tr key={member.id} className="border-b border-border last:border-0">
              <td className="px-3 py-2">
                <Link to={`/contacts/${member.contact.id}`} className="text-foreground hover:underline">
                  {contactDisplayName(member.contact)}
                </Link>
              </td>
              <td className="px-3 py-2 text-muted-foreground">{member.contact.email || '-'}</td>
              <td className="px-3 py-2 text-muted-foreground">{formatPhone(member.contact.phoneNumber) || '-'}</td>
              <td className="px-3 py-2 text-muted-foreground">{member.contact.organizationName || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ListDetailContent({ group, refetch }: { group: ContactGroupDetail | null; refetch: () => void }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { perform: updateGroup, loading: saving } = useUpdateContactGroup()
  const { perform: deleteGroup } = useDeleteContactGroup()
  const { perform: addContact } = useAddGroupContact()
  const { perform: removeContact } = useRemoveGroupContact()

  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [ruleset, setRuleset] = useState<Ruleset>(DEFAULT_RULESET)
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([])

  useEffect(() => {
    if (group) {
      const synced = syncFromGroup(group)
      setName(synced.name)
      setRuleset(synced.ruleset)
      setSelectedContacts(synced.selectedContacts)
    }
  }, [group])

  const startEditing = useCallback(() => {
    if (group) {
      const synced = syncFromGroup(group)
      setName(synced.name)
      setRuleset(synced.ruleset)
      setSelectedContacts(synced.selectedContacts)
    }
    setEditing(true)
  }, [group])

  const cancelEditing = useCallback(() => {
    if (group) {
      const synced = syncFromGroup(group)
      setName(synced.name)
      setRuleset(synced.ruleset)
      setSelectedContacts(synced.selectedContacts)
    }
    setEditing(false)
  }, [group])

  const handleSave = useCallback(async () => {
    if (!id || !group) {
      return
    }
    await updateGroup(id, {
      name: name.trim() || undefined,
      ruleset: group.dynamic ? ruleset : undefined,
    })

    if (!group.dynamic) {
      const originalIds = new Set(group.contacts.map((c) => c.contactId))
      const selectedIds = new Set(selectedContacts.map((c) => c.id))
      const toAdd = selectedContacts.filter((c) => !originalIds.has(c.id))
      const toRemove = [...originalIds].filter((cid) => !selectedIds.has(cid))
      for (const contact of toAdd) {
        await addContact(id, contact.id)
      }
      for (const contactId of toRemove) {
        await removeContact(id, contactId)
      }
    }

    refetch()
    setEditing(false)
  }, [id, group, name, ruleset, selectedContacts, updateGroup, addContact, removeContact, refetch])

  const handleDelete = useCallback(async () => {
    if (!id) {
      return
    }
    await deleteGroup(id)
    navigate('/lists')
  }, [id, deleteGroup, navigate])

  const handleAddContact = useCallback((contact: Contact) => {
    setSelectedContacts((prev) => (prev.some((c) => c.id === contact.id) ? prev : [...prev, contact]))
  }, [])

  const handleRemoveContact = useCallback((contactId: string) => {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId))
  }, [])

  if (!group) {
    return <div className="py-12 text-center text-muted-foreground">List not found</div>
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <div className="flex items-center gap-3">
          <CardTitle>{group.name}</CardTitle>
          <Badge variant={group.dynamic ? 'default' : 'secondary'}>{group.dynamic ? 'Dynamic' : 'Static'}</Badge>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {editing && (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                <X className="mr-1 h-4 w-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="mr-1 h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
          {!editing && (
            <>
              <Button variant="outline" size="sm" onClick={startEditing}>
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
              <ConfirmDialog
                title="Delete list"
                description={`Are you sure you want to delete "${group.name}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                trigger={
                  <Button variant="destructive" size="sm">
                    <Trash2 className="mr-1 h-4 w-4" />
                    Delete
                  </Button>
                }
              />
            </>
          )}
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="pt-6">
        {editing && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-sm font-medium text-muted-foreground">
                Name
              </label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <Separator />

            {group.dynamic && <RuleBuilder ruleset={ruleset} onChange={setRuleset} />}
            {!group.dynamic && (
              <StaticContactPicker
                selectedContacts={selectedContacts}
                onAdd={handleAddContact}
                onRemove={handleRemoveContact}
              />
            )}
          </div>
        )}

        {!editing && group.dynamic && (
          <RuleBuilder ruleset={(group.rules as Ruleset) ?? DEFAULT_RULESET} onChange={() => {}} />
        )}

        {!editing && !group.dynamic && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Members ({group.contacts.length})</label>
            <MemberTable group={group} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function ListDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: group, loading, refetch } = useContactGroup(id)

  if (!loading && !group) {
    return (
      <Page>
        <div className="py-12 text-center text-muted-foreground">List not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/lists">Back to Lists</BackLink>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        }>
        <ListDetailContent group={group} refetch={refetch} />
      </LoadingWrapper>
    </Page>
  )
}
